import React from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box, Grid, Button, Typography, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, Paper, Stack, Chip
} from '@mui/material';
import { getRequest, postRequest, patchRequest } from 'Includes/api/apicall';
import { GET_URL, PATCH_URL } from 'Includes/urls';
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { loadBillingFilters, saveBillingFilters, clearBillingCompanyFilter } from 'Includes/billingFilterStorage';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const FILTER_PAPER_SX = {
  p: 2.5,
  mb: 2,
  borderRadius: 3,
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  border: '1px solid',
  borderColor: 'divider',
};

const TABLE_PAPER_SX = {
  borderRadius: 3,
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'hidden',
};

const DATA_GRID_SX = {
  border: 0,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f5f7fb',
    fontWeight: 600,
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#fafafa',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
  },
};

const ACTION_BTN_SX = {
  borderRadius: 2,
  textTransform: 'none',
  px: 2,
  py: 1,
};

const formatAmount = (v) => {
  const n = parseFloat(v || 0);
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function CompanyPaymentList(props) {
  const navigate = useNavigate();
  const storedFilters = loadBillingFilters();
  const [rows, setRows] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [rowCount, setRowCount] = React.useState(0)
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)
  const [sortModel, setSortModel] = React.useState([
    { field: 'payment_date', sort: 'desc' }
  ])
  const [academicYearList, setAcademicYearList] = React.useState([])
  const [companies, setCompanies] = React.useState([])
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState(storedFilters.academicYearId || '')
  const [selectedCompany, setSelectedCompany] = React.useState(null)
  const [detailOpen, setDetailOpen] = React.useState(false)
  const [detailLoading, setDetailLoading] = React.useState(false)
  const [detailPayment, setDetailPayment] = React.useState(null)
  const [detailItems, setDetailItems] = React.useState([])
  const [fromDate, setFromDate] = React.useState('')
  const [toDate, setToDate] = React.useState('')
  const [methodFilter, setMethodFilter] = React.useState('')
  const [referenceKey, setReferenceKey] = React.useState('')
  const [isGstApplicableFilter, setIsGstApplicableFilter] = React.useState('')
  const [collectedByFilter, setCollectedByFilter] = React.useState('')
  const [users, setUsers] = React.useState([])
  const fileInputRef = React.useRef(null)
  const [uploadRow, setUploadRow] = React.useState(null)
  const [uploadingReceipt, setUploadingReceipt] = React.useState(false)
  const [editDialogOpen, setEditDialogOpen] = React.useState(false)
  const [editingPayment, setEditingPayment] = React.useState(null)
  const [editForm, setEditForm] = React.useState({
    payment_date: '',
    method: 'bank_transfer',
    cheque_status: 'received',
    reference: '',
    notes: '',
    amount: '',
    company_name: '',
    is_gst_applicable: false,
    gst_percentage: '18',
  })
  const [editFormErrors, setEditFormErrors] = React.useState({})
  const [savingEdit, setSavingEdit] = React.useState(false)
  const isSuperuser = React.useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}')
      return Boolean(u?.is_superuser)
    } catch {
      return false
    }
  }, [])

  const METHOD_CHOICES = ['', 'cash', 'bank_transfer_unaccountable', 'bank_transfer', 'upi', 'cheque', 'gateway']
  const CHEQUE_STATUS_CHOICES = [
    { value: 'received', label: 'Received' },
    { value: 'deposited', label: 'Deposited' },
    { value: 'transacted', label: 'Transacted' },
  ]
  const CHEQUE_STATUS_COLORS = {
    received: { bg: '#fef3c7', color: '#b45309' },
    deposited: { bg: '#dbeafe', color: '#1d4ed8' },
    transacted: { bg: '#d1fae5', color: '#047857' },
  }
  const GST_APPLICABLE_CHOICES = [
    { value: '', label: 'All' },
    { value: 'true', label: 'GST Applicable' },
    { value: 'false', label: 'GST Not Applicable' },
  ]

  const columns = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'company_name',
      headerName: 'Company',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => {
        const isManual = Boolean(params.row?.is_manual);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap title={params.value}>
              {params.value || '—'}
            </Typography>
            {isManual && (
              <Chip label="Manual" size="small" color="warning" sx={{ height: 20, flexShrink: 0 }} />
            )}
          </Box>
        );
      },
    },
    { field: 'collected_by_name', headerName: 'Collected By', width: 150 },
    {
      field: 'payment_date',
      headerName: 'Payment Date',
      width: 160,
      valueGetter: (params) => {
        const v = params.row?.payment_date;
        return v ? String(v).slice(0, 16).replace('T', ' ') : '—';
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatAmount(params.value)}
        </Typography>
      ),
    },
    {
      field: 'method',
      headerName: 'Method',
      width: 160,
      renderCell: (params) => (
        <Chip label={params.value || '—'} size="small" variant="outlined" />
      ),
    },
    {
      field: 'cheque_status',
      headerName: 'Cheque Status',
      width: 140,
      renderCell: (params) => {
        if (params.row?.method !== 'cheque') {
          return <Typography variant="body2" color="text.secondary">—</Typography>
        }
        const status = params.value || 'received'
        const label = CHEQUE_STATUS_CHOICES.find((s) => s.value === status)?.label || status
        const colors = CHEQUE_STATUS_COLORS[status] || {}
        return (
          <Chip
            label={label}
            size="small"
            sx={{ fontWeight: 600, bgcolor: colors.bg, color: colors.color }}
          />
        )
      },
    },
    { field: 'reference', headerName: 'Reference', width: 150 },
    { field: 'created_by_name', headerName: 'Created By', width: 140, valueGetter: (params) => params.row?.created_by_name || '-' },
    { field: 'modified_by_name', headerName: 'Modified By', width: 140, valueGetter: (params) => params.row?.modified_by_name || '-' },
    { 
      field: 'is_gst_applicable', 
      headerName: 'GST', 
      width: 90,
      renderCell: (params) => {
        if (params.value == null) {
          return <Chip label="—" size="small" />;
        }
        return (
          <Chip
            label={params.value ? 'Yes' : 'No'}
            size="small"
            color={params.value ? 'success' : 'default'}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 70,
      getActions: (params) => {
        if (params?.row?.is_manual) {
          return [
            <GridActionsCellItem label="View Details" onClick={() => handleViewDetails(params?.row)} showInMenu />,
            <GridActionsCellItem label="Edit" onClick={() => handleEditClick(params?.row)} showInMenu />,
            <GridActionsCellItem
              label="Print Receipt"
              onClick={() => handlePrintManualReceipt(params?.row)}
              showInMenu
            />,
          ];
        }
        return [
          <GridActionsCellItem label="View Details" onClick={() => handleViewDetails(params?.row)} showInMenu />,
          <GridActionsCellItem label="Edit" onClick={() => handleEditClick(params?.row)} showInMenu />,
          <GridActionsCellItem
            label="Generate Paid Invoice"
            onClick={() => handleGeneratePaidInvoice(params?.row)}
            showInMenu
          />,
          <GridActionsCellItem
            label={uploadingReceipt && uploadRow?.id === params?.row?.id ? "Uploading Receipt..." : "Upload Receipt"}
            onClick={() => handleUploadReceiptClick(params?.row)}
            icon={<UploadFileOutlinedIcon />}
            showInMenu
            disabled={uploadingReceipt}
          />,
          <GridActionsCellItem
            label="Download Receipt"
            onClick={() => handleDownloadReceipt(params?.row)}
            icon={<DownloadOutlinedIcon />}
            showInMenu
            disabled={!params?.row?.payment_receipt_details?.file}
          />,
        ];
      }
    }
  ];

  const getPayments = (academicYearId, compId, p = page, ps = pageSize, sort = sortModel) => {
    const paymentParams = {
      ...(academicYearId ? { year: academicYearId } : {}),
      ...(compId ? { company: compId } : {}),
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
      ...(methodFilter ? { method: methodFilter } : {}),
      ...(referenceKey ? { key: referenceKey } : {}),
      ...(collectedByFilter ? { collected_by: collectedByFilter } : {}),
      ...(isGstApplicableFilter ? { is_gst_applicable: isGstApplicableFilter } : {}),
      include_manual: 1,
      limit: ps,
      pageno: (p + 1),
    }
    if (sort && sort.length > 0) {
      const sortItem = sort[0]
      paymentParams.ordering = sortItem.sort === 'desc' ? `-${sortItem.field}` : sortItem.field
    }
    setLoading(true)
    getRequest(GET_URL.payments.api, paymentParams, props)
      .then((payRes) => {
        const payData = payRes?.status === 200
          ? (payRes.data?.data || payRes.data || [])
          : []
        const payCount = payRes?.status === 200
          ? (payRes.data?.count || (Array.isArray(payData) ? payData.length : 0))
          : 0
        setRows(Array.isArray(payData) ? payData : [])
        setRowCount(payCount)
      })
      .finally(() => setLoading(false))
  }

  React.useEffect(() => {
    getAcademicYearList()
    getUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getAcademicYearList = () => {
    const url = GET_URL.academicyear.api;
    getRequest(url, {}, props).then((response) => {
      if (response && response.status === 200) {
        setAcademicYearList(response.data.data);
      }
    });
  };

  const getUsers = () => {
    const url = GET_URL.user.api;
    getRequest(url, {groups: '3,4' }, props).then((response) => {
      if (response && response.status === 200) {
        setUsers(response.data.data || response.data || []);
      }
    });
  };

  const getCompanies = (year) => {
    const url = GET_URL.company_billing_config.api
    getRequest(url, { year }, props)
      .then(response => {
        if (response && response.status === 200) {
          const data = response.data.data || response.data || []
          // Deduplicate by company — multiple billing configs per company otherwise repeat names.
          const optionsMap = new Map()
          ;(Array.isArray(data) ? data : []).forEach((row) => {
            if (row?.company != null && !optionsMap.has(row.company)) {
              optionsMap.set(row.company, row)
            }
          })
          setCompanies(Array.from(optionsMap.values()))
        }
      })
      .catch(() => { })
  }

  // If year is restored from localStorage, load companies automatically.
  React.useEffect(() => {
    if (selectedAcademicYear) {
      saveBillingFilters({ academicYearId: selectedAcademicYear })
      getCompanies(selectedAcademicYear)
      setPage(0)
      getPayments(selectedAcademicYear, selectedCompany?.company || null, 0, pageSize, sortModel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAcademicYear])

  React.useEffect(() => {
    if (!selectedAcademicYear) return
    setPage(0)
    getPayments(selectedAcademicYear, selectedCompany?.company || null, 0, pageSize, sortModel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, methodFilter, referenceKey, isGstApplicableFilter, collectedByFilter, sortModel])

  // Restore company selection after companies are loaded.
  React.useEffect(() => {
    if (!selectedAcademicYear) return
    if (!companies || companies.length === 0) return
    if (selectedCompany) return

    const sf = loadBillingFilters()
    const match =
      (sf.companyId ? companies.find(c => String(c.company) === String(sf.companyId)) : null) ||
      (sf.companyConfigId ? companies.find(c => String(c.id) === String(sf.companyConfigId)) : null)
    if (match) {
      setSelectedCompany(match)
      setPage(0)
      getPayments(selectedAcademicYear, match.company, 0, pageSize, sortModel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies])


  const handleAcademicYearChange = (event) => {
    const yearId = event.target.value;
    setSelectedAcademicYear(yearId);
    clearBillingCompanyFilter()
    setSelectedCompany(null)
    setRows([])
    setPage(0)
    if (yearId) {
      getCompanies(yearId);
      getPayments(yearId, null, 0, pageSize, sortModel)
    }
  };

  const handleCompanyChange = (_e, value) => {
    setSelectedCompany(value)
    if (selectedAcademicYear && value) {
      saveBillingFilters({
        academicYearId: selectedAcademicYear,
        companyId: value.company,
        companyConfigId: value.id,
        companyName: value.company_name,
      })
      setPage(0)
      getPayments(selectedAcademicYear, value.company, 0, pageSize, sortModel)
    } else if (selectedAcademicYear && !value) {
      setPage(0)
      getPayments(selectedAcademicYear, null, 0, pageSize, sortModel)
    }
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
    if (selectedAcademicYear) {
      getPayments(selectedAcademicYear || null, selectedCompany?.company || null, newPage, pageSize, sortModel)
    }
  }

  const handlePageSizeChange = (newPageSize) => {
    const resetPage = 0
    setPageSize(newPageSize)
    setPage(resetPage)
    if (selectedAcademicYear) {
      getPayments(selectedAcademicYear || null, selectedCompany?.company || null, resetPage, newPageSize, sortModel)
    }
  }

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel)
    setPage(0) // Reset to first page when sort changes
    if (selectedAcademicYear) {
      getPayments(selectedAcademicYear, selectedCompany?.company || null, 0, pageSize, newSortModel)
    }
  }

  const goToAdd = () => {
    const pathname = `/billing/payment?year=${selectedAcademicYear}&company=${selectedCompany.company}`
    navigate(pathname)
  }

  const goToManualPayment = () => {
    navigate('/billing/payment?manual=1')
  }

  const handleDownloadExcel = () => {
    if (!selectedAcademicYear) return
    const params = {
      ...(selectedAcademicYear ? { year: selectedAcademicYear } : {}),
      ...(selectedCompany?.company ? { company: selectedCompany.company } : {}),
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
      ...(methodFilter ? { method: methodFilter } : {}),
      ...(referenceKey ? { key: referenceKey } : {}),
      ...(isGstApplicableFilter ? { is_gst_applicable: isGstApplicableFilter } : {}),
      ...(collectedByFilter ? { collected_by: collectedByFilter } : {}),
    }
    const url = GET_URL.payments.api
    const reqProps = { ...props, responseType: 'blob' }
    getRequest(url, { ...params, export: 1 }, reqProps).then((response) => {
      if (response && response.status === 200) {
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
        const fileURL = window.URL.createObjectURL(blob)
        const contentDisposition =
          response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition']
        const match = typeof contentDisposition === 'string'
          ? contentDisposition.match(/filename="?([^"]+)"?/i)
          : null
        const filename = match?.[1] || `payments_year_${selectedAcademicYear}.csv`
        const link = document.createElement('a')
        link.href = fileURL
        link.download = filename
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(fileURL)
        document.body.removeChild(link)
      }
    })
  }

  const handleViewDetails = (row) => {
    if (!row?.id) return
    setDetailOpen(true)
    setDetailLoading(true)
    setDetailPayment(null)
    setDetailItems([])
    if (row.is_manual) {
      const mid = row.raw_id || String(row.id).replace(/^manual-/, '')
      getRequest(`${GET_URL.manual_payments.api}${mid}/`, {}, props)
        .then((response) => {
          const payload = response?.data?.data || response?.data
          if (!payload) return
          setDetailPayment({ ...payload, is_manual: true })
          setDetailItems([])
        })
        .finally(() => setDetailLoading(false))
      return
    }
    getRequest(`${GET_URL.payments.api}${row.id}/`, {}, props)
      .then((response) => {
        const payload = response?.data?.data || response?.data
        if (!payload) return
        setDetailPayment(payload)
        const items = Array.isArray(payload.items) ? payload.items : []
        setDetailItems(items)
      })
      .finally(() => setDetailLoading(false))
  }

  const handleEditClick = (row) => {
    if (!row?.id) return
    setEditingPayment(row)
    
    // Format payment_date for datetime-local input (YYYY-MM-DDTHH:mm)
    let formattedDate = ''
    if (row.payment_date) {
      const date = new Date(row.payment_date)
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`
      }
    }
    
    setEditForm({
      payment_date: formattedDate,
      method: row.method || 'bank_transfer',
      cheque_status: row.cheque_status || 'received',
      reference: row.reference || '',
      notes: row.notes || '',
      amount: row.is_manual && row.amount != null ? String(row.amount) : '',
      company_name: row.is_manual ? (row.company_name || '') : '',
      is_gst_applicable: row.is_manual ? Boolean(row.is_gst_applicable) : false,
      gst_percentage: row.is_manual && row.gst_percentage != null ? String(row.gst_percentage) : '18',
    })
    setEditFormErrors({})
    setEditDialogOpen(true)
  }

  const handleEditClose = () => {
    setEditDialogOpen(false)
    setEditingPayment(null)
    setEditForm({
      payment_date: '',
      method: 'bank_transfer',
      cheque_status: 'received',
      reference: '',
      notes: '',
      amount: '',
      company_name: '',
      is_gst_applicable: false,
      gst_percentage: '18',
    })
    setEditFormErrors({})
    setSavingEdit(false)
  }

  const handleEditFieldChange = (e) => {
    const { name, value } = e.target
    let next = value
    if (name === 'is_gst_applicable') {
      next = value === true || value === 'true'
    }
    setEditForm(prev => {
      const updated = { ...prev, [name]: next }
      if (name === 'method') {
        updated.cheque_status = next === 'cheque' ? (prev.cheque_status || 'received') : 'received'
      }
      return updated
    })
    // Clear error for this field
    if (editFormErrors[name]) {
      setEditFormErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const validateEditForm = () => {
    const errors = {}
    let isValid = true

    if (!editForm.payment_date || editForm.payment_date.trim() === '') {
      errors.payment_date = 'Payment Date is required'
      isValid = false
    }

    if (editingPayment?.is_manual) {
      const amount = parseFloat(editForm.amount || 0)
      if (isNaN(amount) || amount <= 0) {
        errors.amount = 'Amount is required and must be greater than 0'
        isValid = false
      }
      if (!editForm.company_name || !String(editForm.company_name).trim()) {
        errors.company_name = 'Company name is required'
        isValid = false
      }
      if (editForm.is_gst_applicable) {
        const gstPct = parseFloat(editForm.gst_percentage)
        if (isNaN(gstPct) || gstPct < 0 || gstPct > 100) {
          errors.gst_percentage = 'GST % must be between 0 and 100'
          isValid = false
        }
      }
    }

    setEditFormErrors(errors)
    return isValid
  }

  const handleEditSave = () => {
    if (!validateEditForm() || !editingPayment?.id) return
    
    setSavingEdit(true)
    const payload = {
      payment_date: editForm.payment_date,
      method: editForm.method,
      cheque_status: editForm.method === 'cheque' ? (editForm.cheque_status || 'received') : 'received',
      reference: editForm.reference || '',
      notes: editForm.notes || '',
    }
    if (editingPayment.is_manual) {
      payload.amount = parseFloat(editForm.amount)
      payload.company_name = String(editForm.company_name || '').trim()
      payload.is_gst_applicable = Boolean(editForm.is_gst_applicable)
      payload.gst_percentage = editForm.is_gst_applicable
        ? parseFloat(editForm.gst_percentage || 18)
        : null
    }

    const isManual = Boolean(editingPayment.is_manual)
    const editId = isManual
      ? (editingPayment.raw_id || String(editingPayment.id).replace(/^manual-/, ''))
      : editingPayment.id
    const url = isManual
      ? `${PATCH_URL.manual_payments.api}${editId}/`
      : `${PATCH_URL.payments.api}${editId}/`

    patchRequest(url, payload, props)
      .then((response) => {
        setSavingEdit(false)
        if (response && response.status === 200) {
          Swal.fire({
            position: 'top-end',
            type: 'success',
            title: 'Success',
            text: 'Payment updated successfully',
            showConfirmButton: false,
            timer: 1500,
          })
          handleEditClose()
          // Refresh the payment list
          if (selectedAcademicYear) {
            getPayments(selectedAcademicYear || null, selectedCompany?.company || null, page, pageSize, sortModel)
          }
        }
      })
      .catch((error) => {
        setSavingEdit(false)
        const errorMessage = error?.response?.data?.Reason || 'Failed to update payment'
        Swal.fire({
          type: 'error',
          title: 'Error',
          text: errorMessage,
        })
      })
  }

  const handleUploadReceiptClick = (row) => {
    if (!row?.id) return
    setUploadRow(row)
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleReceiptFileSelected = (e) => {
    const file = e.target.files?.[0]
    if (!file || !uploadRow?.id) return

    const payload = new FormData()
    payload.append('file', file)
    setUploadingReceipt(true)

    patchRequest(`${GET_URL.payments.api}${uploadRow.id}/?upload_receipt=1`, payload, props)
      .then((response) => {
        if (response && response.status === 200) {
          if (selectedAcademicYear) {
            getPayments(selectedAcademicYear || null, selectedCompany?.company || null, page, pageSize, sortModel)
          }
          // If detail modal is open for this payment, refresh it
          if (detailOpen && detailPayment?.id && String(detailPayment.id) === String(uploadRow.id)) {
            handleViewDetails(uploadRow)
          }
          Swal.fire({
            position: 'top-end',
            type: 'success',
            title: 'Success',
            text: 'Uploaded Payment bill successfully',
            showConfirmButton: false,
            timer: 1500,
          });
        }
      })
      .finally(() => {
        setUploadingReceipt(false)
        setUploadRow(null)
        if (e?.target) e.target.value = ''
      })
  }

  const handleDownloadReceipt = (row) => {
    const url = row?.payment_receipt_details?.file
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handlePrintManualReceipt = (row) => {
    const mid = row?.raw_id || String(row?.id || '').replace(/^manual-/, '')
    if (!mid) return
    const url = `${GET_URL.manual_payments.api}${mid}/`
    getRequest(url, { generate_pdf: 1 }, { ...props, responseType: 'blob' })
      .then((pdfResponse) => {
        if (!(pdfResponse && pdfResponse.status === 200)) {
          Swal.fire({
            type: 'error',
            title: 'Unable to print',
            text: 'Failed to generate payment receipt PDF.',
          })
          return
        }
        const blob = new Blob([pdfResponse.data], { type: 'application/pdf' })
        const fileURL = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = fileURL
        link.download = `manual_payment_receipt_${mid}.pdf`
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(fileURL)
        document.body.removeChild(link)
      })
      .catch(() => {
        Swal.fire({
          type: 'error',
          title: 'Unable to print',
          text: 'Failed to generate payment receipt PDF.',
        })
      })
  }

  const handleGeneratePaidInvoice = (row) => {
    if (!row?.id) return
    getRequest(`${GET_URL.payments.api}${row.id}/`, {}, props)
      .then((response) => {
        const payload = response?.data?.data || response?.data
        const items = Array.isArray(payload?.items) ? payload.items : []
        const firstInvoice = items.find((it) => it?.invoice)
        if (!firstInvoice?.invoice) {
          Swal.fire({
            type: 'warning',
            title: 'Invoice not found',
            text: 'No linked invoice found for this payment.',
          })
          return
        }
        const url = `${GET_URL.invoices.api}${firstInvoice.invoice}/`
        return getRequest(url, { generate_pdf: 1 }, { ...props, responseType: 'blob' }).then((pdfResponse) => {
          if (!(pdfResponse && pdfResponse.status === 200)) return
          const blob = new Blob([pdfResponse.data], { type: 'application/pdf' })
          const fileURL = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = fileURL
          link.download = `paid_invoice_${firstInvoice.invoice_number || firstInvoice.invoice}.pdf`
          document.body.appendChild(link)
          link.click()
          window.URL.revokeObjectURL(fileURL)
          document.body.removeChild(link)
        })
      })
      .catch(() => {
        Swal.fire({
          type: 'error',
          title: 'Unable to generate',
          text: 'Failed to generate paid invoice PDF.',
        })
      })
  }

  const handleCloseDetails = () => {
    setDetailOpen(false)
    setDetailPayment(null)
    setDetailItems([])
    setDetailLoading(false)
  }

  return (
    <Box sx={{ p: 3 }}>
  
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleReceiptFileSelected}
      />
  
      <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            Payments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Filter collections by year and company, or record a manual payment.
          </Typography>
        </Grid>

        <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
            {isSuperuser && (
              <Button
                variant="outlined"
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleDownloadExcel}
                disabled={!selectedAcademicYear}
                sx={ACTION_BTN_SX}
              >
                Export
              </Button>
            )}
            {selectedCompany?.company && selectedAcademicYear && (
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineOutlined />}
                onClick={goToAdd}
                sx={ACTION_BTN_SX}
              >
                Add for company
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineOutlined />}
              onClick={goToManualPayment}
              sx={{ ...ACTION_BTN_SX, boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}
            >
              Manual Payment
            </Button>
          </Stack>
        </Grid>
      </Grid>
  
      <Paper elevation={0} sx={FILTER_PAPER_SX}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                onChange={handleAcademicYearChange}
                label="Academic Year"
              >
                {academicYearList.map((year) => (
                  <MenuItem key={year.id} value={year.id}>
                    {year.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
  
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={companies}
              getOptionLabel={(o) => o.company_name || ''}
              isOptionEqualToValue={(option, value) => String(option?.company) === String(value?.company)}
              value={selectedCompany}
              onChange={handleCompanyChange}
              renderInput={(params) => (
                <TextField {...params} label="Company (Optional)" size="small" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="From"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
  
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="To"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
  
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Method</InputLabel>
              <Select
                value={methodFilter}
                onChange={(e) => setMethodFilter(e.target.value)}
                label="Method"
              >
                {METHOD_CHOICES.map(m => (
                  <MenuItem key={m || 'all'} value={m}>
                    {m || 'All'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
  
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>GST</InputLabel>
              <Select
                value={isGstApplicableFilter}
                onChange={(e) => setIsGstApplicableFilter(e.target.value)}
                label="GST"
              >
                {GST_APPLICABLE_CHOICES.map(c => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
  
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Reference"
              value={referenceKey}
              onChange={(e) => setReferenceKey(e.target.value)}
              size="small"
              fullWidth
            />
          </Grid>
  
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Collected By</InputLabel>
              <Select
                value={collectedByFilter}
                onChange={(e) => setCollectedByFilter(e.target.value)}
                label="Collected By"
              >
                <MenuItem value="">All</MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.username || u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Showing {rows.length} of {rowCount} payment{rowCount === 1 ? '' : 's'}
              {selectedCompany?.company_name ? ` · ${selectedCompany.company_name}` : ''}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
  
      <Paper elevation={0} sx={TABLE_PAPER_SX}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 20, 50]}
          rowCount={rowCount}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          sortingMode="server"
          autoHeight
          rowHeight={44}
          disableSelectionOnClick
          sx={DATA_GRID_SX}
        />
      </Paper>
  
      {/* DETAILS DIALOG */}
      <Dialog open={detailOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Payment Details</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Typography>Loading...</Typography>
          ) : detailPayment && (
            <Box>
              <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
                <Typography fontWeight={700}>{detailPayment.company_name || '—'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatAmount(detailPayment.amount)}
                  {detailPayment.method ? ` · ${detailPayment.method}` : ''}
                  {detailPayment.reference ? ` · Ref: ${detailPayment.reference}` : ''}
                  {detailPayment.is_manual
                    ? ` · GST: ${detailPayment.is_gst_applicable ? 'Yes' : 'No'}`
                    : ''}
                </Typography>
              </Box>
  
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2}>No invoice mappings</TableCell>
                    </TableRow>
                  ) : (
                    detailItems.map((it, i) => (
                      <TableRow key={i}>
                        <TableCell>{it.invoice_number}</TableCell>
                        <TableCell align="right">{formatAmount(it.amount_paid)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {detailPayment?.is_manual && (
            <Button
              variant="outlined"
              onClick={() => handlePrintManualReceipt(detailPayment)}
              sx={{ textTransform: 'none', mr: 'auto' }}
            >
              Print Receipt
            </Button>
          )}
          <Button onClick={handleCloseDetails} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
  
      {/* EDIT DIALOG */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingPayment?.is_manual ? 'Edit Manual Payment' : 'Edit Payment'}
        </DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            {editingPayment?.is_manual && (
              <TextField
                name="company_name"
                label="Company name"
                value={editForm.company_name}
                onChange={handleEditFieldChange}
                size="small"
                fullWidth
                required
                error={!!editFormErrors.company_name}
                helperText={editFormErrors.company_name || 'Manual entry (not linked to company master)'}
              />
            )}
            {editingPayment?.is_manual && (
              <TextField
                name="amount"
                label="Amount (₹)"
                value={editForm.amount}
                onChange={handleEditFieldChange}
                size="small"
                fullWidth
                error={!!editFormErrors.amount}
                helperText={editFormErrors.amount}
              />
            )}
            {editingPayment?.is_manual && (
              <FormControl size="small" fullWidth>
                <InputLabel>GST Applicable</InputLabel>
                <Select
                  name="is_gst_applicable"
                  value={editForm.is_gst_applicable ? 'true' : 'false'}
                  label="GST Applicable"
                  onChange={(e) => {
                    handleEditFieldChange({
                      target: {
                        name: 'is_gst_applicable',
                        value: e.target.value === 'true',
                      },
                    })
                  }}
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </Select>
              </FormControl>
            )}
            {editingPayment?.is_manual && editForm.is_gst_applicable && (
              <TextField
                name="gst_percentage"
                label="GST %"
                value={editForm.gst_percentage}
                onChange={handleEditFieldChange}
                size="small"
                fullWidth
                error={!!editFormErrors.gst_percentage}
                helperText={editFormErrors.gst_percentage || 'Amount is treated as inclusive of GST'}
              />
            )}
            {!editingPayment?.is_manual && editingPayment?.company_name && (
              <Typography variant="body2" color="text.secondary">
                Company: <strong>{editingPayment.company_name}</strong>
              </Typography>
            )}
            <TextField
              type="datetime-local"
              name="payment_date"
              label="Payment Date"
              value={editForm.payment_date}
              onChange={handleEditFieldChange}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
  
            <FormControl size="small" fullWidth>
              <InputLabel>Method</InputLabel>
              <Select
                name="method"
                value={editForm.method}
                onChange={handleEditFieldChange}
                label="Method"
              >
                {METHOD_CHOICES.filter(m => m).map(m => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {editForm.method === 'cheque' && (
              <FormControl size="small" fullWidth>
                <InputLabel>Cheque Status</InputLabel>
                <Select
                  name="cheque_status"
                  value={editForm.cheque_status || 'received'}
                  onChange={handleEditFieldChange}
                  label="Cheque Status"
                >
                  {CHEQUE_STATUS_CHOICES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              name="reference"
              value={editForm.reference}
              onChange={handleEditFieldChange}
              size="small"
              fullWidth
              label="Reference"
              error={!!editFormErrors.reference}
              helperText={editFormErrors.reference}
            />
  
            <TextField
              name="notes"
              value={editForm.notes}
              onChange={handleEditFieldChange}
              multiline
              rows={3}
              size="small"
              fullWidth
              label="Notes"
            />
          </Box>
        </DialogContent>
  
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleEditClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={savingEdit} sx={ACTION_BTN_SX}>
            {savingEdit ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
  
    </Box>
  );
}

export default CompanyPaymentList;


